import * as React from "react";
import { Box } from "../components/ui/box";
import { useElementSize } from "./use-element-size";

const SM_BREAKPOINT = 644;
const MD_BREAKPOINT = 794;

export type ContentBreakpoint = "sm" | "md" | "lg";

const BREAKPOINT_ORDER: Record<ContentBreakpoint, number> = {
  sm: 0,
  md: 1,
  lg: 2,
};

type ContentSizeValue = {
  width: number;
  breakpoint: ContentBreakpoint;
  /** True when the current breakpoint is at or below the given size (e.g. `isAtOrBelow("md")` is true at `"sm"` or `"md"`). */
  isAtOrBelow: (size: ContentBreakpoint) => boolean;
};

function computeBreakpoint(width: number): ContentBreakpoint {
  if (width <= 0) return "lg";
  if (width < SM_BREAKPOINT) return "sm";
  if (width < MD_BREAKPOINT) return "md";
  return "lg";
}

function toValue(width: number): ContentSizeValue {
  const breakpoint = computeBreakpoint(width);
  return {
    width,
    breakpoint,
    isAtOrBelow: (size) => BREAKPOINT_ORDER[breakpoint] <= BREAKPOINT_ORDER[size],
  };
}

const ContentSizeContext = React.createContext<ContentSizeValue | null>(null);

export function ContentSizeProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  // A width commit here re-renders every `useContentSize` consumer;
  // `useElementSize` holds the commits through a layout-settle window and
  // publishes once when it closes.
  const { ref, size } = useElementSize<HTMLDivElement>({ box: "content" });
  const width = size.width;

  const value = React.useMemo(() => toValue(width), [width]);

  return (
    <Box fill ref={ref}>
      <ContentSizeContext.Provider value={value}>
        {children}
      </ContentSizeContext.Provider>
    </Box>
  );
}

// Window-width fallback for consumers outside a ContentSizeProvider.
function subscribeToResize(callback: () => void): () => void {
  window.addEventListener("resize", callback);
  return () => window.removeEventListener("resize", callback);
}

function getWindowWidth(): number {
  return window.innerWidth;
}

function getServerWidth(): number {
  return 0;
}

/**
 * Returns the current content-area size and breakpoint. Falls back to window
 * width when used outside a `ContentSizeProvider` (e.g. mobile layouts, tests).
 */
export function useContentSize(): ContentSizeValue {
  const ctx = React.useContext(ContentSizeContext);
  const windowWidth = React.useSyncExternalStore(
    subscribeToResize,
    getWindowWidth,
    getServerWidth,
  );
  if (ctx && ctx.width > 0) return ctx;
  return toValue(windowWidth);
}

export function useContentBreakpoint(): Omit<ContentSizeValue, "width"> {
  const { breakpoint, isAtOrBelow } = useContentSize();
  return { breakpoint, isAtOrBelow };
}
